/**
 * NextStepCard - the AI brief on a lead's page (docs/plans/crm-brief-plan.md A3 step 3, B6, B8).
 *
 * Sits at the top of the lead page's rail: the one next thing to do, why (the why-line), the lead's
 * or a recruiter's own words as a verbatim quote when there is one, and the time it is due. It
 * never sends, books or closes anything, and it carries no draft: a person acts (plan A0).
 *
 * It renders NOTHING when briefs are off (`enabled` false, or the server answers 503
 * BRIEF_DISABLED), when the lead has no brief (404), or before the first read; a failed read is
 * quiet too, so the rail never grows an error box for a card that is only a hint. It refetches on
 * the lead's content-free contact-change ping (the same one CommsThread listens to; the engine
 * and the routes emit it with { brief: true }), so a fresh brief appears without a reload. It adds
 * no poll and no API call beyond the three brief routes.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { RefreshCw, Sparkles, ThumbsDown, ThumbsUp } from 'lucide-react';
import { subscribeContactChanges } from '../../lib/realtimeTransport';
import { getPref, setPref } from '../lib';
import { briefErrorMessage, getBrief, isBriefDisabled, refreshBrief, sendBriefFeedback } from './api';
import {
  BRIEF_REFRESH_COOLDOWN_MS,
  briefAgeLabel,
  briefTone,
  clockLabel,
  nextKindLabel,
} from './briefLogic';
import type { Brief, BriefFeedbackVerdict } from './types';

/** "Re-briefing..." is shown from the click until the next change ping, or this long, first. */
const REBRIEFING_MAX_MS = 60_000;

const TONE_PILL: Record<'red' | 'amber' | 'neutral', string> = {
  red: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  amber: 'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  neutral: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
};

/** "Sonnet 4.6" from `us.anthropic.claude-sonnet-4-6`; '' when the id is unknown. */
export function modelShortName(modelId: string | null | undefined): string {
  const m = /(sonnet|opus|haiku|fable)-(\d+)(?:-(\d+))?/i.exec(modelId || '');
  if (!m) return '';
  const family = m[1].charAt(0).toUpperCase() + m[1].slice(1).toLowerCase();
  return `${family} ${m[2]}${m[3] ? `.${m[3]}` : ''}`;
}

/** "3:00 pm" for today, "Sep 30, 3:00 pm" otherwise; '' when the value is not a date. */
function dueLabel(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const today = d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  if (today) return clockLabel(d);
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${clockLabel(d)}`;
}

/** Seconds to wait from a 429's Retry-After header, or null when it is absent or unreadable. */
function retryAfterMs(e: unknown): number | null {
  const h = (e as any)?.response?.headers;
  const raw = h ? (h['retry-after'] ?? h['Retry-After']) : null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n * 1000 : null;
}

const voteKey = (runId: string): string => `brief.vote.${runId}`;

const readVote = (runId: string | undefined): BriefFeedbackVerdict | null => {
  if (!runId) return null;
  const v = getPref(voteKey(runId), '');
  return v === 'up' || v === 'down' ? v : null;
};

export interface NextStepCardProps {
  contactId: string;
  /** The worklist's `brief.enabled`; false renders nothing and fetches nothing. */
  enabled: boolean;
}

export default function NextStepCard({ contactId, enabled }: NextStepCardProps) {
  const [brief, setBrief] = useState<Brief | null>(null);
  const [vote, setVote] = useState<BriefFeedbackVerdict | null>(null);
  const [rebriefing, setRebriefing] = useState(false);
  const [cooling, setCooling] = useState(false);
  const [voting, setVoting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const alive = useRef(true);
  const seq = useRef(0);
  const rebriefTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const coolTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = useCallback(() => {
    if (rebriefTimer.current) clearTimeout(rebriefTimer.current);
    if (coolTimer.current) clearTimeout(coolTimer.current);
    rebriefTimer.current = null;
    coolTimer.current = null;
  }, []);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      clearTimers();
    };
  }, [clearTimers]);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    try {
      const res = await getBrief(contactId);
      if (!alive.current || mine !== seq.current) return;
      setBrief(res.state === 'off' ? null : res.brief);
      setVote(readVote(res.brief?.run_id));
    } catch (e) {
      if (!alive.current || mine !== seq.current) return;
      // Off, or a failed read: a hint card stays out of the way. Keep what is shown on a blip.
      if (isBriefDisabled(e)) setBrief(null);
    }
  }, [contactId]);

  // A different lead starts clean: no stale brief, vote, lock or message from the last one.
  useEffect(() => {
    seq.current += 1;
    clearTimers();
    setBrief(null);
    setVote(null);
    setRebriefing(false);
    setCooling(false);
    setVoting(false);
    setNotice(null);
  }, [contactId, enabled, clearTimers]);

  useEffect(() => {
    if (!enabled || !contactId) return undefined;
    load();
    return subscribeContactChanges(contactId, () => {
      // The re-brief landed (or anything else on the lead changed): read again, drop the label.
      if (rebriefTimer.current) {
        clearTimeout(rebriefTimer.current);
        rebriefTimer.current = null;
      }
      setRebriefing(false);
      load();
    });
  }, [contactId, enabled, load]);

  const onRebrief = async () => {
    if (rebriefing || cooling) return;
    setNotice(null);
    setRebriefing(true);
    setCooling(true);
    if (rebriefTimer.current) clearTimeout(rebriefTimer.current);
    if (coolTimer.current) clearTimeout(coolTimer.current);
    rebriefTimer.current = setTimeout(() => {
      rebriefTimer.current = null;
      if (alive.current) setRebriefing(false);
    }, REBRIEFING_MAX_MS);
    coolTimer.current = setTimeout(() => {
      coolTimer.current = null;
      if (alive.current) setCooling(false);
    }, BRIEF_REFRESH_COOLDOWN_MS);
    try {
      await refreshBrief(contactId);
    } catch (e) {
      if (!alive.current) return;
      if (rebriefTimer.current) clearTimeout(rebriefTimer.current);
      rebriefTimer.current = null;
      setRebriefing(false);
      if ((e as any)?.response?.status === 429) {
        // Locked for as long as the server says (its window), never shorter than we know of.
        const wait = retryAfterMs(e) ?? BRIEF_REFRESH_COOLDOWN_MS;
        if (coolTimer.current) clearTimeout(coolTimer.current);
        coolTimer.current = setTimeout(() => {
          coolTimer.current = null;
          if (alive.current) setCooling(false);
        }, wait);
      } else {
        if (coolTimer.current) clearTimeout(coolTimer.current);
        coolTimer.current = null;
        setCooling(false);
      }
      setNotice(briefErrorMessage(e, 'Could not re-brief this lead. Please try again.'));
    }
  };

  const onVote = async (verdict: BriefFeedbackVerdict) => {
    if (!brief?.run_id || vote || voting) return;
    setVoting(true);
    setNotice(null);
    try {
      await sendBriefFeedback(contactId, { run_id: brief.run_id, verdict });
      setPref(voteKey(brief.run_id), verdict);
      if (alive.current) setVote(verdict);
    } catch (e) {
      if (alive.current) setNotice(briefErrorMessage(e, 'Could not save your vote. Please try again.'));
    } finally {
      if (alive.current) setVoting(false);
    }
  };

  if (!enabled || !brief) return null;

  const tone = briefTone(brief.urgency);
  const due = dueLabel(brief.due_at);
  const quoteWho = brief.quote_speaker === 'lead' ? 'Their words' : brief.quote_speaker === 'recruiter' ? 'Recruiter' : '';
  const footer = ['Suggested by AI', modelShortName(brief.model_id), briefAgeLabel(brief)].filter(Boolean).join(' · ');
  const iconBtn =
    'inline-flex items-center justify-center min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 sm:h-8 sm:w-8 rounded-md border text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60 disabled:cursor-not-allowed';

  return (
    <section
      data-testid="next-step-card"
      aria-label="Next step"
      className="mb-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 space-y-2"
    >
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" aria-hidden="true" />
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Next step</h3>
        <span
          data-testid="next-step-pill"
          className={`ml-auto inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${TONE_PILL[tone]}`}
        >
          {nextKindLabel(brief.next_kind)}
        </span>
      </div>

      <p data-testid="next-step-why" className="text-sm text-gray-800 dark:text-gray-200">
        {brief.why}
      </p>

      {brief.quote ? (
        <blockquote
          data-testid="next-step-quote"
          className="border-l-2 border-gray-300 dark:border-gray-600 pl-2 text-sm italic text-gray-600 dark:text-gray-300"
        >
          {quoteWho ? (
            <span className="block not-italic text-xs font-medium text-gray-500 dark:text-gray-400">{quoteWho}</span>
          ) : null}
          {brief.quote}
        </blockquote>
      ) : null}

      {due ? (
        <p data-testid="next-step-due" className="text-xs text-gray-600 dark:text-gray-300">
          due {due}
        </p>
      ) : null}

      {notice ? (
        <p data-testid="next-step-notice" role="status" className="text-xs text-gray-500 dark:text-gray-400">
          {notice}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-2 pt-1">
        <span data-testid="next-step-footer" className="text-xs text-gray-500 dark:text-gray-400">
          {footer}
        </span>
        <div className="ml-auto flex items-center gap-1.5">
          <button
            type="button"
            data-testid="next-step-rebrief"
            onClick={onRebrief}
            disabled={rebriefing || cooling}
            className="inline-flex items-center justify-center gap-1.5 min-h-[44px] sm:min-h-0 sm:h-8 px-2.5 rounded-md border border-gray-200 dark:border-gray-600 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${rebriefing ? 'animate-spin' : ''}`} aria-hidden="true" />
            {rebriefing ? 'Re-briefing…' : 'Re-brief'}
          </button>
          <button
            type="button"
            data-testid="next-step-up"
            aria-label="Helpful"
            aria-pressed={vote === 'up'}
            title="Helpful"
            onClick={() => onVote('up')}
            disabled={!!vote || voting}
            className={iconBtn}
          >
            <ThumbsUp
              className={`h-4 w-4 ${vote === 'up' ? 'fill-current text-blue-600 dark:text-blue-400' : ''}`}
              aria-hidden="true"
            />
          </button>
          <button
            type="button"
            data-testid="next-step-down"
            aria-label="Not helpful"
            aria-pressed={vote === 'down'}
            title="Not helpful"
            onClick={() => onVote('down')}
            disabled={!!vote || voting}
            className={iconBtn}
          >
            <ThumbsDown
              className={`h-4 w-4 ${vote === 'down' ? 'fill-current text-blue-600 dark:text-blue-400' : ''}`}
              aria-hidden="true"
            />
          </button>
        </div>
      </div>
    </section>
  );
}
